import React, { useState, useEffect } from 'react';
import { Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Typography, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { CssTextField } from 'pages/reference-implementation/components/select';
import Btn from 'components/button';
import { RIStyles } from 'pages/reference-implementation/Style';
import { commonStyles as style } from 'assets/style';
import { InputInfo } from 'pages/reference-implementation/components/input-info';

const SNS = (props) => {
    const { resource, handleDrawerAction } = props;
    const [subscriptions, setSubscriptions] = useState(resource?.configureData?.subscriptions || []);
    const [editingIndex, setEditingIndex] = useState(null);
    const [touched, setTouched] = useState(false);
    const [rowTouched, setRowTouched] = useState({});
    const [errors, setErrors] = useState({});

    const subscriptionsApi = useMutation(() => axios.get(endPoints.aws.lookups.snsSubscriptions));

    useEffect(() => {
        subscriptionsApi.mutate();
    }, []);

    useEffect(() => {
        if (subscriptionsApi?.isSuccess && subscriptionsApi?.data?.data?.length) {
            const apiData = subscriptionsApi?.data?.data.filter((item) => !['lambda', 'sqs'].includes(item.value?.toLowerCase()));
            if (!resource?.configureData?.subscriptions?.length) {
                const initialSubscriptions = apiData.map((item) => ({
                    protocol: item.name,
                    protocolValue: item.value,
                    endpointValue: '',
                    selected: true,
                }));
                setSubscriptions(initialSubscriptions);
            }
        }
    }, [subscriptionsApi?.isSuccess]);

    const handleEndpointChange = (index, value) => {
        setSubscriptions((prev) => prev?.map((row, i) => (i === index ? { ...row, endpointValue: value } : row)));
        setRowTouched((prev) => ({ ...prev, [index]: true }));
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
    };

    const handleEditConfirm = (index) => {
        setRowTouched((prev) => ({ ...prev, [index]: true }));
        // Keep the field editable so the user can fix an invalid value without re-clicking edit.
        if (!errors[index]) {
            setEditingIndex(null);
        }
    };

    const handleDelete = (index) => {
        setSubscriptions((prev) => prev?.map((row, i) => (i === index ? { ...row, endpointValue: '' } : row)));
        if (editingIndex === index) setEditingIndex(null);
    };

    const validateForm = (subs) => {
        const newErrors = {};
        subs.forEach((row, index) => {
            if (!row.endpointValue?.trim()) {
                newErrors[index] = `Endpoint is required for ${row.protocol}.`;
            } else if (row.protocolValue === 'email' && /\s/.test(row.endpointValue)) {
                newErrors[index] = 'Email must not contain spaces.';
            } else if (row.protocolValue === 'email' && !/^dl/i.test(row.endpointValue?.trim())) {
                newErrors[index] =
                    'The SNS topic endpoint should be configured to use the application team’s DL (distribution list) rather than an individual email address. This ensures that notifications are routed to the appropriate team and remain manageable even when team members change.';
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        setTouched(true);
        const isValid = validateForm(subscriptions);
        if (!isValid) return;
        handleDrawerAction('save', { subscriptions }, { isValid: true });
    };

    useEffect(() => {
        validateForm(subscriptions);
    }, [subscriptions]);

    const showNoData = subscriptionsApi?.isError || (!subscriptionsApi?.isLoading && !subscriptions?.length);

    return (
        <Box style={RIStyles.tags.wrapper}>
            <Box sx={RIStyles.tags.contentWrapper}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0047BA', mb: 1, fontSize: '1rem' }}>
                            Subscriptions Management
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {showNoData ? (
                            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No subscription protocols available.
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#EEF4FF' }}>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.08em', color: '#000' }}>
                                                PROTOCOL
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.08em', color: '#000' }}>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    ENDPOINT
                                                    <InputInfo
                                                        maxWidth="400px"
                                                        title="The SNS topic endpoint should be configured to use the application team’s DL (distribution list) rather than an individual email address. This ensures that notifications are routed to the appropriate team and remain manageable even when team members change."
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subscriptions.map((row, index) => (
                                            <TableRow key={row.protocolValue}>
                                                <TableCell sx={{ fontSize: '0.875rem' }}>{row.protocol}</TableCell>
                                                <TableCell>
                                                    <CssTextField
                                                        size="small"
                                                        placeholder="Endpoint Value"
                                                        value={row.endpointValue}
                                                        onChange={(e) => handleEndpointChange(index, e.target.value)}
                                                        disabled={editingIndex !== index}
                                                        onBlur={() => handleEditConfirm(index)}
                                                        error={(touched || rowTouched[index]) && !!errors[index]}
                                                        helperText={(touched || rowTouched[index]) && errors[index] ? errors[index] : ''}
                                                        sx={{
                                                            width: '100%',
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                WebkitTextFillColor: row.endpointValue ? '#1a1a1a' : '#9e9e9e',
                                                                fontWeight: row.endpointValue ? 500 : 400,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled': {
                                                                backgroundColor: row.endpointValue ? '#fff' : '#f5f5f5',
                                                                '& fieldset': {
                                                                    borderColor: row.endpointValue ? '#0047BA' : '#e0e0e0',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title="Click to edit the endpoint value for this subscription protocol">
                                                        <IconButton size="small" onClick={() => handleEdit(index)} sx={{ color: '#0047BA' }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Click to clear the endpoint value for this subscription protocol">
                                                        <IconButton size="small" onClick={() => handleDelete(index)} sx={{ color: '#0047BA' }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>
                </Grid>
            </Box>
            <Box className="footer" sx={RIStyles.tags.actionWrapper} gap={2}>
                <Btn className="custom-btn" variant="contained" sx={style.modalSaveButton} onClick={handleSubmit} data-testid="save-btn">
                    Save
                </Btn>
                <Btn
                    className="custom-btn"
                    variant="outlined"
                    sx={style.modalCancelButton}
                    onClick={() => handleDrawerAction('cancel')}
                    data-testid="cancel-btn"
                >
                    Cancel
                </Btn>
            </Box>
        </Box>
    );
};

SNS.propTypes = {
    resource: PropTypes.any.isRequired,
    handleDrawerAction: PropTypes.func.isRequired,
};

export default SNS;
